import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { fetchRoomMessages } from '@/redux/thunks/chatThunks';
import type { ChatMessage } from '@/redux/slices/chatSlice';
import { useSocket } from '@/contexts/SocketContext';
import { LoadingSpinner } from '@/components';
import { formatName } from '@/utils/formatName';
import ChatMessageBubble from './ChatMessageBubble';
import MegaphoneComposer from './MegaphoneComposer';
import './ChatRoom.scss';

const MAX_CHARS = 200;
// Emit chat-typing at most once per this interval per local user.
// Server also throttles, but client-side throttle saves socket round-trips
// on heavy keystroke streams.
const TYPING_EMIT_INTERVAL_MS = 1500;
// Emit chat-stop-typing after this many ms of keystroke silence.
const TYPING_IDLE_MS = 3000;
// Cross-room global-megaphone banner auto-dismiss after this many ms.
// Long enough to read a 200-char message, short enough to not linger.
const MEGAPHONE_BANNER_TTL_MS = 60_000;

interface TypingUser {
  userId: string;
  username: string;
  displayName?: string;
}

const ChatRoom: React.FC = () => {
  const dispatch = useAppDispatch();
  const { socket } = useSocket();

  const activeRoomId = useAppSelector((s) => s.chat.activeRoomId);
  const rooms = useAppSelector((s) => s.chat.rooms);
  const messages = useAppSelector((s) =>
    activeRoomId ? (s.chat.messagesByRoom[activeRoomId] ?? []) : []
  );
  const hasMore = useAppSelector((s) =>
    activeRoomId ? (s.chat.hasMoreByRoom[activeRoomId] ?? false) : false
  );
  const loading = useAppSelector((s) => s.chat.messagesLoading);

  const [input, setInput] = useState('');
  const [showMegaphone, setShowMegaphone] = useState(false);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [banner, setBanner] = useState<ChatMessage | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const currentUserId = useAppSelector((s) => s.auth?.user?.id);
  // Throttle bookkeeping for outgoing typing events.
  const lastTypingEmitRef = useRef(0);
  const stopTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Auto-dismiss timer for the cross-room megaphone banner.
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeRoom = rooms.find((r) => r.id === activeRoomId);
  const remaining = MAX_CHARS - input.length;

  // Scroll to bottom when new messages arrive (only if already near bottom)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (isNearBottom) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Listen for cross-room global-megaphone banners. The originating
  // room's listener filters its own broadcast out — that already
  // rendered inline as a normal new-chat-message.
  useEffect(() => {
    if (!socket || !activeRoomId) return;

    const onBanner = (msg: ChatMessage) => {
      if (msg.originRoomId === activeRoomId) return; // already in our message list
      setBanner(msg);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = setTimeout(() => setBanner(null), MEGAPHONE_BANNER_TTL_MS);
    };

    socket.on('chat-megaphone-banner', onBanner);
    return () => {
      socket.off('chat-megaphone-banner', onBanner);
    };
  }, [socket, activeRoomId]);

  // Clear banner when switching rooms — banners are scoped to wherever
  // the user is now, not "wherever they were when it arrived."
  useEffect(() => {
    setBanner(null);
    if (bannerTimerRef.current) {
      clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = null;
    }
  }, [activeRoomId]);

  // Presence: subscribe to member count + typing updates for the active room.
  // Reset state when the room changes so we don't show stale numbers/names.
  useEffect(() => {
    setMemberCount(null);
    setTypingUsers([]);

    if (!socket || !activeRoomId) return;

    const handleRoomMembers = (payload: { roomId: string; count: number }) => {
      if (payload.roomId === activeRoomId) setMemberCount(payload.count);
    };
    const handleTypingUpdate = (payload: { roomId: string; users: TypingUser[] }) => {
      if (payload.roomId !== activeRoomId) return;
      // Filter out the current user — typing-indicator UI is "who else is
      // typing", and the local user sees their own keystrokes already.
      setTypingUsers(payload.users.filter((u) => u.userId !== currentUserId));
    };

    socket.on('room-members', handleRoomMembers);
    socket.on('chat-typing-update', handleTypingUpdate);

    return () => {
      socket.off('room-members', handleRoomMembers);
      socket.off('chat-typing-update', handleTypingUpdate);
    };
  }, [socket, activeRoomId, currentUserId]);

  const emitStopTyping = useCallback(() => {
    if (stopTypingTimerRef.current) {
      clearTimeout(stopTypingTimerRef.current);
      stopTypingTimerRef.current = null;
    }
    lastTypingEmitRef.current = 0;
    if (socket && activeRoomId) socket.emit('chat-stop-typing', activeRoomId);
  }, [socket, activeRoomId]);

  // Stop-typing on room change or unmount so the previous room doesn't
  // keep showing us as typing after we navigate away.
  useEffect(() => emitStopTyping, [emitStopTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value.slice(0, MAX_CHARS);
    setInput(next);
    if (!socket || !activeRoomId) return;

    if (next.trim().length === 0) {
      emitStopTyping();
      return;
    }

    // Throttle outbound typing events (server throttles too but this saves
    // an emit per keystroke).
    const now = Date.now();
    if (now - lastTypingEmitRef.current >= TYPING_EMIT_INTERVAL_MS) {
      lastTypingEmitRef.current = now;
      socket.emit('chat-typing', activeRoomId);
    }

    // Reset the idle stop-typing timer.
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(emitStopTyping, TYPING_IDLE_MS);
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !activeRoomId || !socket) return;
    socket.emit('chat-message', { roomId: activeRoomId, body: trimmed });
    setInput('');
    emitStopTyping();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const typingLabel = useMemo(() => {
    if (typingUsers.length === 0) return null;
    const names = typingUsers.map((u) => formatName({ displayName: u.displayName, username: u.username, limit: 16 }));
    if (names.length === 1) return `${names[0]} is typing…`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
    return `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length - 2 === 1 ? '' : 's'} are typing…`;
  }, [typingUsers]);

  const handleLoadMore = () => {
    if (!activeRoomId || loading) return;
    dispatch(fetchRoomMessages({ roomId: activeRoomId, append: true }));
  };

  if (!activeRoomId) {
    return (
      <div className="chat-room chat-room--empty">
        <p>Select a room to start chatting</p>
      </div>
    );
  }

  return (
    <div className="chat-room">
      {/* Header */}
      <header className="chat-room__header">
        <span className="chat-room__hash">#</span>
        <h2 className="chat-room__name">{activeRoom?.name ?? '…'}</h2>
        {activeRoom?.description && (
          <span className="chat-room__desc">{activeRoom.description}</span>
        )}
        {memberCount !== null && (
          <span className="chat-room__members" title={`${memberCount} member${memberCount === 1 ? '' : 's'} online`}>
            <span className="chat-room__members-dot" /> {memberCount}
          </span>
        )}
      </header>

      {/* Cross-room megaphone banner — only renders for global megaphones from OTHER rooms */}
      {banner && (
        <div className="chat-room__banner" role="alert">
          <span className="chat-room__banner-icon">📣</span>
          <div className="chat-room__banner-content">
            <span className="chat-room__banner-from">
              {banner.displayName || banner.username}
              {banner.originRoomSlug && (
                <span className="chat-room__banner-origin"> in #{banner.originRoomSlug}</span>
              )}
            </span>
            <span className="chat-room__banner-body">{banner.body}</span>
          </div>
          <button
            type="button"
            className="chat-room__banner-close"
            onClick={() => {
              setBanner(null);
              if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
            }}
            aria-label="Dismiss megaphone"
          >
            ×
          </button>
        </div>
      )}

      {/* Message list */}
      <div className="chat-room__messages" ref={listRef}>
        {hasMore && (
          <button
            className="chat-room__load-more"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Load older messages'}
          </button>
        )}

        {loading && messages.length === 0 ? (
          <LoadingSpinner size="medium" message="Loading messages…" />
        ) : messages.length === 0 ? (
          <p className="chat-room__empty">No messages yet — say hello!</p>
        ) : (
          messages.map((msg) => <ChatMessageBubble key={msg._id} message={msg} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* Megaphone modal */}
      {showMegaphone && (
        <div className="chat-room__megaphone-overlay">
          <MegaphoneComposer onClose={() => setShowMegaphone(false)} />
        </div>
      )}

      {/* Typing indicator */}
      {typingLabel && (
        <div className="chat-room__typing" aria-live="polite">
          <span className="chat-room__typing-dots">
            <span /><span /><span />
          </span>
          {typingLabel}
        </div>
      )}

      {/* Input bar */}
      <div className="chat-room__input-bar">
        <button
          className="chat-room__megaphone-btn"
          onClick={() => setShowMegaphone(true)}
          title="Send a megaphone message to all users"
        >
          📣
        </button>
        <div className="chat-room__input-wrap">
          <textarea
            className="chat-room__input"
            placeholder={`Message #${activeRoom?.name ?? ''}…`}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <span className={`chat-room__counter ${remaining < 20 ? 'warning' : ''}`}>
            {remaining}
          </span>
        </div>
        <button
          className="chat-room__send-btn"
          onClick={handleSend}
          disabled={!input.trim()}
          aria-label="Send message"
        >
          ➤
        </button>
      </div>
    </div>
  );
};

export default ChatRoom;
